// SkyviewFpsCamera.ts
import * as THREE from 'three';
import { Vector3 } from 'three';
import { v4 as uuidv4 } from 'uuid'; // or any uuid helper you use

import { CameraTransformation } from '../../../../typings/api/skymap/rest/v0/.common';
import type { SkyviewOrthographicCamera } from './skyview-orthographic-camera';
import type { SkyviewPerspectiveCamera } from './skyview-perspective-camera';
import { CameraType, FpsCameraParameters, SkyviewCamera } from './types'; // adjust relative path

export class SkyviewFpsCamera extends THREE.PerspectiveCamera implements SkyviewCamera {
  readonly uuid = uuidv4();
  readonly cameraType = CameraType.FPSCamera;

  private readonly dummyBox = new THREE.Box3(); // scratch box, avoids GC
  private lastTransformTime = Date.now();

  private _yaw = 0;
  private _pitch = 0;
  private readonly maxPitch = Math.PI / 2 - 0.01; // ≈  + 89 °
  private readonly minPitch = -Math.PI / 2 + 0.01; // ≈  – 89 °

  private readonly maxFov = 90;
  private readonly minFov = 20;

  get yaw() {
    return this._yaw;
  }
  get pitch() {
    return this._pitch;
  }

  constructor(fov = 75, aspect = 1, near = 0.1, far = 20000) {
    super(fov, aspect, near, far);

    this.matrixAutoUpdate = true;
  }

  move(delta: THREE.Vector3): void {
    this.position.add(delta);
    this.touch();
  }

  rotate(phiDelta: number, thetaDelta: number): void {
    this._yaw += phiDelta;
    this._pitch += thetaDelta;
    this._pitch = THREE.MathUtils.clamp(this._pitch, this.minPitch, this.maxPitch);

    this.quaternion.setFromEuler(new THREE.Euler(this._pitch, this._yaw, 0, 'YXZ'));
    this.touch();
  }

  /** Mouse-wheel / pinch zoom. Returns the _unused_ part of delta, like your orbit camera. */
  zooming(deltaZoom: number): number {
    const prevFov = this.fov;
    const newFov = THREE.MathUtils.clamp(prevFov + deltaZoom * 0.05, this.minFov, this.maxFov);
    this.fov = newFov;
    this.updateProjectionMatrix();
    this.touch();
    return deltaZoom - (newFov - prevFov) / 0.05;
  }

  setZoom(fov: number): void {
    this.fov = THREE.MathUtils.clamp(fov, this.minFov, this.maxFov);
    this.updateProjectionMatrix();
    this.touch();
  }

  /* ──────────────── housekeeping ─────────────────────────── */
  timeSinceTransformed(): number {
    return Date.now() - this.lastTransformTime;
  }
  private touch() {
    this.lastTransformTime = Date.now();
  }

  updateAspect(width: number, height: number): void {
    this.aspect = width / height;
    this.updateProjectionMatrix();
  }

  setParameters(parameters: FpsCameraParameters): void {
    this.aspect = parameters.aspect;
    this.setZoom(this.fov);
  }

  getZoomAmountPercent() {
    return 1.0 - (this.fov - this.minFov) / (this.maxFov - this.minFov);
  }

  /* ──────────────── math helpers ─────────────────────────── */
  getDistanceToPoint(v: THREE.Vector3): number {
    return v.distanceTo(this.position);
  }
  getDistanceToBoundingBox(box: THREE.Box3): number {
    return box.getCenter(this.dummyBox.min).distanceTo(this.position);
  }

  focusOnBox(box: THREE.Box3, offset = 1): void {
    // FPS camera doesn’t orbit, so we simply teleport backwards along –Z
    const size = box.getSize(this.dummyBox.min).length();
    const center = box.getCenter(new THREE.Vector3());
    const back = new THREE.Vector3(0, 0, size * offset).applyQuaternion(this.quaternion);

    this.position.copy(center.clone().add(back));
    this.lookAt(center);
    this._yaw = this.rotation.y;
    this._pitch = this.rotation.x;
    this.touch();
  }

  getScreenPosition(point: THREE.Vector3): THREE.Vector2 {
    const v = point.clone().project(this);
    return new THREE.Vector2((v.x + 1) * 0.5, (1 - v.y) * 0.5);
  }
  getLookAtPosition(): THREE.Vector3 {
    return new THREE.Vector3(0, 0, -1)
      .applyQuaternion(this.quaternion)
      .add(this.getWorldPosition(new Vector3()));
  }

  getFrustum(): THREE.Frustum {
    return new THREE.Frustum().setFromProjectionMatrix(
      new THREE.Matrix4().multiplyMatrices(this.projectionMatrix, this.matrixWorldInverse),
    );
  }
  getRay(x: number, y: number): THREE.Raycaster {
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(x, y), this);
    return raycaster;
  }

  setPosition(offset: THREE.Vector2): void {
    // Used by some HUD logic in the orbit camera.
    // For FPS we treat it as NOP to keep behaviour predictable.
  }

  copyViewFromCamera(camera: SkyviewCamera): void {
    switch (camera.cameraType) {
      case CameraType.FPSCamera: {
        const fpsCamera = camera as SkyviewFpsCamera;

        this.setParameters({ aspect: fpsCamera.aspect });

        break;
      }
      case CameraType.PerspectiveCamera: {
        const perspectiveCamera = camera as SkyviewPerspectiveCamera;
        this.setParameters({ aspect: perspectiveCamera.aspect });
        break;
      }
      case CameraType.OrthographicCamera: {
        const orthographicCamera = camera as unknown as SkyviewOrthographicCamera;

        this.setParameters({
          aspect: orthographicCamera.getAspectRatio(),
        });

        break;
      }
      default:
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        throw new Error(`Unknown camera type: ${camera.cameraType}`);
    }
    this.updateProjectionMatrix();

    const e = new THREE.Euler().setFromQuaternion(camera.getTHREECamera().quaternion, 'YXZ');
    this._yaw = e.y;
    this._pitch = THREE.MathUtils.clamp(e.x, this.minPitch, this.maxPitch);
    this.touch();
  }

  addToScene(scene: THREE.Scene): void {
    scene.add(this);
  }
  removeFromScene(): void {
    this.removeFromParent();
  }

  transformTo(pos?: THREE.Vector3, rot?: THREE.Quaternion, duration = 0): void {
    if (!pos && !rot) {
      return;
    }
    if (duration <= 0) {
      if (pos) {
        this.position.copy(pos);
      }
      if (rot) {
        this.quaternion.copy(rot);
      }
      this.touch();
      return;
    }
    const startPos = this.position.clone();
    const startRot = this.quaternion.clone();
    const endPos = pos ?? startPos;
    const endRot = rot ?? startRot;
    const start = performance.now();

    const step = () => {
      const t = THREE.MathUtils.clamp((performance.now() - start) / duration, 0, 1);
      this.position.lerpVectors(startPos, endPos, t);
      this.quaternion.slerpQuaternions(startRot, endRot, t);
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        this.touch();
      }
    };
    requestAnimationFrame(step);
  }

  getTHREECamera(): THREE.PerspectiveCamera {
    return this;
  }

  extractTransformation(): CameraTransformation {
    return {
      position: this.position.toArray() as [number, number, number],
      rotation: this.quaternion.toArray() as [number, number, number, number],
      fov: this.fov,
    } as unknown as CameraTransformation; // adapt to your exact schema
  }

  applyTransformation(t: CameraTransformation): void {
    this.position.fromArray([t.position.x, t.position.y, t.position.z]);
    this.quaternion.fromArray([t.rotation.x, t.rotation.y, t.rotation.z]);
    this.fov = (t as any).fov ?? this.fov;
    this.updateProjectionMatrix();

    // sync yaw / pitch
    const e = new THREE.Euler().setFromQuaternion(this.quaternion, 'YXZ');
    this._yaw = e.y;
    this._pitch = THREE.MathUtils.clamp(e.x, this.minPitch, this.maxPitch);
    this.touch();
  }
}
